import { Card, formatDate, FormLines, StaticField, useTestIds } from "@homebound/beam";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import {
  ApplyConfigurationCard_AggregateVersionFragment,
  ApplyConfigurationCard_ReadyPlanConfigLikeFragment,
  ApplyConfigurationCard_ReadyPlanOptionFragment,
} from "./ApplyConfigurationCard.generated";

type ApplyConfigurationCardProps = {
  readyPlanConfig: ApplyConfigurationCard_ReadyPlanConfigLikeFragment;
  /** Because Product Offering Config is not pinned to a specific version, callers should pass the version details separately */
  aggregateVersion: ApplyConfigurationCard_AggregateVersionFragment | undefined;
};

/** Displays a ready plan configuration's cover photo, plan and design selections. */
export function ApplyConfigurationCard(props: ApplyConfigurationCardProps) {
  const { readyPlanConfig, aggregateVersion } = props;
  const { name, updatedAt, productOffering, readyPlan, readyPlanOptions } = readyPlanConfig;
  const tid = useTestIds({}, "applyConfigurationCard");
  const fields = [
    { label: "Plan", value: readyPlan?.name },
    { label: "Elevation", value: optionName(readyPlanOptions, (o) => o.type.isElevation) },
    { label: "Ext. Scheme", value: optionName(readyPlanOptions, (o) => o.type.isExteriorScheme) },
    { label: "Int. Scheme", value: optionName(readyPlanOptions, (o) => o.type.isInteriorScheme) },
    { label: "Spec Level", value: optionName(readyPlanOptions, (o) => o.type.isSpecLevel) },
    { label: "Version", value: aggregateVersion?.displayVersion ?? "-" },
    { label: "Updated", value: formatDate(updatedAt.toPlainDate(), "shortDate") },
  ];
  return (
    <Card
      bordered={true}
      fullWidth={true}
      imageFit="cover"
      imgSrc={productOffering?.coverAsset?.previewUrl ?? PLAN_FALLBACK_IMG}
      subtitle={readyPlan?.code ?? ""}
      title={name ?? ""}
      detailContent={
        <FormLines width="sm" labelStyle="left" gap={0}>
          {fields.map((field) => (
            <StaticField key={field.label} label={field.label} value={field.value ?? "-"} />
          ))}
        </FormLines>
      }
      {...tid}
    />
  );
}

/** Returns the `maybeShortName` of the first option matching `fn`, e.g. the config's elevation. */
function optionName(
  options: ApplyConfigurationCard_ReadyPlanOptionFragment[],
  fn: (option: ApplyConfigurationCard_ReadyPlanOptionFragment) => boolean,
): string | undefined {
  return options.find(fn)?.maybeShortName;
}
